import {
  createEventTicket,
  deactivateEventTicket,
  updateEventTicket,
  type AdminTicketType,
} from "../api/tickets";

const CATALOG_CATEGORIES = new Set([
  "full_conference",
  "single_day",
  "add_on",
  "extra_page",
  "additional_paper",
  "test",
]);

export interface TicketDraftRow {
  id?: number;
  key: string;
  name: string;
  description: string;
  priceMajor: string;
  currency: string;
}

export function isSimpleTicket(ticket: AdminTicketType): boolean {
  return !ticket.category || !CATALOG_CATEGORIES.has(ticket.category);
}

export function ticketsToDraftRows(tickets: AdminTicketType[]): TicketDraftRow[] {
  return tickets
    .filter((t) => t.status === "active" && isSimpleTicket(t))
    .map((t) => ({
      id: t.id,
      key: `ticket-${t.id}`,
      name: t.name,
      description: t.description ?? "",
      priceMajor: (t.pricePence / 100).toFixed(2),
      currency: t.currency,
    }));
}

export function emptyTicketRow(): TicketDraftRow {
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name: "",
    description: "",
    priceMajor: "",
    currency: "gbp",
  };
}

function parsePriceMajor(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export async function deactivateSimpleTickets(
  existingTickets: AdminTicketType[],
): Promise<void> {
  for (const ticket of existingTickets) {
    if (ticket.status === "active" && isSimpleTicket(ticket)) {
      await deactivateEventTicket(ticket.id);
    }
  }
}

export async function syncTicketModeCatalog(
  eventId: number,
  rows: TicketDraftRow[],
  existingTickets: AdminTicketType[],
): Promise<void> {
  const validRows = rows.filter((r) => r.name.trim());
  const keepIds = new Set(
    validRows.map((r) => r.id).filter((id): id is number => id !== undefined),
  );

  for (const existing of existingTickets) {
    if (
      existing.status === "active" &&
      isSimpleTicket(existing) &&
      !keepIds.has(existing.id)
    ) {
      await deactivateEventTicket(existing.id);
    }
  }

  for (const row of validRows) {
    const pricePence = parsePriceMajor(row.priceMajor);
    if (pricePence === null) {
      throw new Error(`Enter a valid price for "${row.name.trim()}"`);
    }

    const input = {
      name: row.name.trim(),
      description: row.description.trim() || undefined,
      pricePence,
      currency: row.currency.toLowerCase(),
      status: "active" as const,
    };

    if (row.id) {
      await updateEventTicket(row.id, input);
    } else {
      await createEventTicket(eventId, input);
    }
  }
}
